import { lazy, Suspense } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { idbPersister } from "@/lib/idb-persister";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { AuthProvider } from "@/contexts/AuthContext";
import AppShell from "@/components/AppShell.phase1";
import AdminRoute from "@/components/AdminRoute";
import { Loader2 } from "lucide-react";

const Auth = lazy(() => import("@/pages/Auth"));
const Chats = lazy(() => import("@/pages/Chats"));
const ChatRoom = lazy(() => import("@/pages/ChatRoom"));
const NewChat = lazy(() => import("@/pages/NewChat"));
const CreateGroup = lazy(() => import("@/pages/CreateGroup"));
const CreateChannel = lazy(() => import("@/pages/CreateChannel"));
const Dating = lazy(() => import("@/pages/Dating"));
const Matches = lazy(() => import("@/pages/Matches"));
const Profile = lazy(() => import("@/pages/Profile"));
const Settings = lazy(() => import("@/pages/Settings"));
const Admin = lazy(() => import("@/pages/Admin"));
const Banking = lazy(() => import("@/pages/Premium"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const TermsAndConditions = lazy(() => import("@/pages/TermsAndConditions"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const ReportAbuse = lazy(() => import("@/pages/ReportAbuse"));
const StarredMessages = lazy(() => import("@/pages/StarredMessages"));
const JoinChannel = lazy(() => import("@/pages/JoinChannel"));
const Orders = lazy(() => import("@/pages/Orders"));
const BusinessProfile = lazy(() => import("@/pages/BusinessProfile"));
const ServiceBot = lazy(() => import("@/pages/ServiceBot"));
const ContactUs = lazy(() => import("@/pages/ContactUs"));
const CallHistory = lazy(() => import("@/pages/CallHistory"));
const HelpGuide = lazy(() => import("@/pages/HelpGuide"));
const ProductHuntKit = lazy(() => import("@/pages/ProductHuntKit"));
const XLaunchKit = lazy(() => import("@/pages/XLaunchKit"));
const Gallery = lazy(() => import("@/pages/Gallery"));
const Leaderboard = lazy(() => import("@/pages/Leaderboard"));
const ConnectDashboard = lazy(() => import("@/pages/ConnectDashboard"));
const ConnectStorefront = lazy(() => import("@/pages/ConnectStorefront"));
const ProgressierKit = lazy(() => import("@/pages/ProgressierKit"));
const SellerFulfillment = lazy(() => import("@/pages/SellerFulfillment"));
const ExploreChannels = lazy(() => import("@/pages/ExploreChannels"));
const InviteResolver = lazy(() => import("@/pages/InviteResolver"));
const Landing = lazy(() => import("@/pages/Landing"));
const Index = lazy(() => import("@/pages/Index"));

const PageLoader = () => (
  <div className="flex items-center justify-center h-screen w-full">
    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
  </div>
);


export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 1000 * 60 * 60 * 24, // 24 hours
      staleTime: 1000 * 60 * 5,     // 5 minutes
      retry: 2,
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10000),
    },
    mutations: {
      retry: 1,
    },
  },
});

const persistOptions = {
  persister: idbPersister,
  maxAge: 1000 * 60 * 60 * 24, // 24 hours
};


const App = () => (
  <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
    <TooltipProvider>
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/landing" element={<Landing />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/channel/join/:token" element={<JoinChannel />} />
            <Route path="/invite/:token" element={<InviteResolver />} />
            <Route path="/chat/service-bot" element={<ServiceBot />} />
            <Route path="/terms" element={<TermsAndConditions />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/report-abuse" element={<ReportAbuse />} />
            <Route element={<AppShell />}>
              <Route path="/chats" element={<Chats />} />
              <Route path="/chat/:id" element={<ChatRoom />} />
              <Route path="/new-chat" element={<NewChat />} />
              <Route path="/create-group" element={<CreateGroup />} />
              <Route path="/create-channel" element={<CreateChannel />} />
              <Route path="/explore-channels" element={<ExploreChannels />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/dating" element={<Dating />} />
              <Route path="/matches" element={<Matches />} />
              <Route path="/premium" element={<Banking />} />
              <Route path="/banking" element={<Banking />} />
              <Route element={<AdminRoute />}>
                <Route path="/admin" element={<Admin />} />
                <Route path="/admin/product-hunt" element={<ProductHuntKit />} />
                <Route path="/admin/x-launch" element={<XLaunchKit />} />
                <Route path="/admin/progressier" element={<ProgressierKit />} />
              </Route>
              <Route path="/starred-messages" element={<StarredMessages />} />
              <Route path="/orders" element={<Orders />} />
              <Route path="/call-history" element={<CallHistory />} />
              <Route path="/business/:userId" element={<BusinessProfile />} />
              <Route path="/help-guide" element={<HelpGuide />} />
              <Route path="/gallery" element={<Gallery />} />
              <Route path="/leaderboard" element={<Leaderboard />} />
              <Route path="/connect-dashboard" element={<ConnectDashboard />} />
              <Route path="/fulfillment" element={<SellerFulfillment />} />
            </Route>
            {/* Storefront is outside AppShell — public page */}
            <Route path="/storefront/:accountId" element={<ConnectStorefront />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
    </ThemeProvider>
  </PersistQueryClientProvider>
);

export default App;
